import { useCallback, useEffect, useMemo } from 'react';
import { StaffProfessionControllerApi } from '../api/generated';
import type {
  CreateStaffProfessionRequest,
  StaffProfessionResponse,
  UpdateStaffProfessionRequest
} from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector, useAppStore } from '../store/hooks';
import {
  removeCompetence,
  selectHasFetchedStaffCompetences,
  selectStaffCompetences,
  selectStaffCompetencesError,
  selectStaffCompetencesLoading,
  setError,
  setLoading,
  setCompetences,
  upsertCompetence
} from '../store/staffCompetences/staffCompetencesSlice';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UseStaffCompetencesResult {
  competences: StaffProfessionResponse[];
  isLoading: boolean;
  loadError: string | null;
  refresh: () => Promise<void>;
  createCompetence: (
    payload: CreateStaffProfessionRequest
  ) => Promise<StaffProfessionResponse>;
  updateCompetence: (
    id: string,
    payload: UpdateStaffProfessionRequest
  ) => Promise<StaffProfessionResponse>;
  deleteCompetence: (id: string) => Promise<void>;
}

export function useStaffCompetences(): UseStaffCompetencesResult {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const competences = useAppSelector(selectStaffCompetences);
  const isLoading = useAppSelector(selectStaffCompetencesLoading);
  const loadError = useAppSelector(selectStaffCompetencesError);
  const api = useMemo(() => new StaffProfessionControllerApi(createApiConfiguration()), []);

  const refresh = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const response = await api.listStaffProfessions({});
      dispatch(setCompetences(response.data));
    } catch (e) {
      dispatch(setError(getErrorMessage(e, 'Failed to load staff competences')));
      throw e;
    }
  }, [api, dispatch]);

  useEffect(() => {
    // Read through the store rather than the selector values above: several
    // consumers can mount in the same commit — the week calendar modal and the
    // Gantt week header do — and each one's captured values still say "not
    // loading" until React re-renders it. getState() sees the load the first of
    // them just started, so only one request goes out. A per-instance ref could
    // not do this: every instance has its own.
    const state = store.getState();
    if (selectStaffCompetencesLoading(state)) return;
    // A list that failed to load is NOT usable, so the next consumer to mount
    // retries — otherwise a transient error would strand every reader on an
    // empty list until a browser reload.
    const hasUsableList =
      selectHasFetchedStaffCompetences(state) && selectStaffCompetencesError(state) === null;
    if (hasUsableList) return;
    refresh().catch(() => {
      // error already surfaced via slice state
    });
  }, [store, refresh]);

  const createCompetence = useCallback(
    async (payload: CreateStaffProfessionRequest) => {
      const response = await api.createStaffProfession({
        createStaffProfessionRequest: payload
      });
      dispatch(upsertCompetence(response.data));
      return response.data;
    },
    [api, dispatch]
  );

  const updateCompetence = useCallback(
    async (id: string, payload: UpdateStaffProfessionRequest) => {
      const response = await api.updateStaffProfession({
        professionId: id,
        updateStaffProfessionRequest: payload
      });
      dispatch(upsertCompetence(response.data));
      return response.data;
    },
    [api, dispatch]
  );

  // Backend has no hard delete — archive is the soft-delete equivalent and the only
  // available operation. The list endpoint defaults to excluding archived entries, so we
  // remove from local cache after archive to keep things in sync.
  const deleteCompetence = useCallback(
    async (id: string) => {
      await api.archiveStaffProfession({ professionId: id });
      dispatch(removeCompetence(id));
    },
    [api, dispatch]
  );

  return {
    competences,
    isLoading,
    loadError,
    refresh,
    createCompetence,
    updateCompetence,
    deleteCompetence
  };
}
